/** Locale helpers + money/number/date display bound to the active i18n language. */
import {
  formatDate as fmtDate,
  formatMKD,
  formatNumber as fmtNumber,
  type SupportedLocale,
} from '@gym/i18n';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from './cn.js';

const SUPPORTED: SupportedLocale[] = ['mk', 'sq', 'en'];

/** The active locale, narrowed to a SupportedLocale (defaults to mk). */
export function useLocale(): SupportedLocale {
  const { i18n } = useTranslation();
  const lng = i18n.language?.slice(0, 2) as SupportedLocale;
  return SUPPORTED.includes(lng) ? lng : 'mk';
}

/** Imperative formatters bound to the active locale (for charts/tooltips). */
export function useFormatters(): {
  money: (minor: number) => string;
  number: (n: number, o?: Intl.NumberFormatOptions) => string;
  date: (v: Date | string | number, o?: Intl.DateTimeFormatOptions) => string;
} {
  const locale = useLocale();
  return {
    money: (minor: number) => formatMKD(Math.round(minor), locale),
    number: (n: number, o?: Intl.NumberFormatOptions) => fmtNumber(n, locale, o),
    date: (v: Date | string | number, o?: Intl.DateTimeFormatOptions) => {
      const d = v instanceof Date ? v : new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return fmtDate(d, locale, o);
    },
  };
}

/** Renders integer minor units (денари) as localized MKD; tabular figures. */
export function Money({
  minor,
  className,
  muted,
}: {
  minor: number | null | undefined;
  className?: string;
  muted?: boolean;
}): ReactNode {
  const locale = useLocale();
  return (
    <span className={cn('tnum font-semibold', muted && 'text-muted', className)}>
      {formatMKD(Math.round(minor ?? 0), locale)}
    </span>
  );
}
